/**
 * HoverTag — the Search.Inc standard hover-tag primitive.
 *
 * IJ-097 · 2026-07-25. Codifies the hover-tag style the operator ratified
 * on 2026-07-25 (attached reference image, KnowledgeGraphPage.jsx "Don't
 * be Evil" chip). Every new hover-tag surface must import this
 * component. Do not hand-roll a hover tag with ad-hoc classes.
 *
 * LOCKED STYLE (do not vary without an operator directive):
 *   panel : bg-gray-800  ·  rounded-lg  ·  border border-slate-700/60  ·  shadow-xl
 *   body  : text-white   ·  text-base   ·  leading-relaxed
 *   pad   : p-5
 *   width : w-max max-w-md  (hugs the copy · caps at 28rem so long copy wraps)
 *   pos   : below trigger by default, offset mt-2
 *   fade  : opacity 0 → 100 with a 4-pixel lift, 200 ms transition
 *
 * Inline link colour convention: text-cyan-400 hover:text-cyan-300 — apply
 * to any <a> or <button> passed inside `content`.
 *
 * Interaction: by default the tag is `pointer-events-none` so clicks pass
 * through to whatever sits below it (important on the login page where
 * the username tag can visually overlap the password input while hovered
 * — clicks still land on the password field). Set `interactive` when the
 * tag itself contains clickable content.
 *
 * Props:
 *   children             — the trigger element (input, button, icon, etc.)
 *   content              — string or ReactNode rendered inside the tag
 *   side                 — 'bottom' (default) | 'top' | 'left' | 'right'
 *   align                — 'left' (default) | 'right' | 'center'
 *   interactive          — false (default) | true (only when tag has links)
 *   className            — extra classes on the wrapper (rare)
 *   contentClassName     — extra classes on the tag itself (rare)
 *   testId               — data-testid on wrapper; the tag itself becomes
 *                          `${testId}-content` for programmatic assertions
 */
import React from 'react';

const SIDE_POS = {
  bottom: 'top-full mt-2',
  top: 'bottom-full mb-2',
  right: 'left-full ml-2',
  left: 'right-full mr-2',
};

const ALIGN_POS = {
  left: 'left-0',
  right: 'right-0',
  center: 'left-1/2 -translate-x-1/2',
};

export const HoverTag = ({
  children,
  content,
  side = 'bottom',
  align = 'left',
  interactive = false,
  className = '',
  contentClassName = '',
  testId,
}) => {
  const sidePos = SIDE_POS[side] || SIDE_POS.bottom;
  const alignPos = ALIGN_POS[align] || ALIGN_POS.left;
  const pointerRule = interactive
    ? 'pointer-events-none group-hover:pointer-events-auto'
    : 'pointer-events-none';

  const tagClasses = [
    'absolute z-50',
    sidePos,
    alignPos,
    'w-max max-w-md',
    'rounded-lg border border-slate-700/60 bg-gray-800',
    'p-5 shadow-xl',
    'text-base leading-relaxed text-white',
    'opacity-0 group-hover:opacity-100',
    'translate-y-1 group-hover:translate-y-0',
    'transition-all duration-200',
    pointerRule,
    contentClassName,
  ].filter(Boolean).join(' ');

  return (
    <div className={`group relative ${className}`} data-testid={testId}>
      {children}
      <div
        role="tooltip"
        data-testid={testId ? `${testId}-content` : undefined}
        className={tagClasses}
      >
        {content}
      </div>
    </div>
  );
};

export default HoverTag;
